import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faCommentDots,
  faEdit,
  faExternalLinkAlt,
  faFileLines,
  faFolderOpen,
  faLightbulb,
  faRobot,
  faRotateLeft,
  faSpinner,
  faStickyNote,
  faTrash,
  faUserMd,
} from '@fortawesome/free-solid-svg-icons';

import { useGetPresignedClaimFile } from '../../../services/claims';
import {
  groupDbqEvidenceFilesByFolder,
  getWorkspacePersonLabel,
  getWorkspaceRequestContentionRows,
  getWorkspaceToeSummary,
} from './dbqWorkspaceUtils';
import DbqRequestRecentActivity from './DbqRequestRecentActivity';
import DbqSubmissionPacketPanel from './DbqSubmissionPacketPanel';

const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)';

const STATUS_ACCENT_COLORS = {
  primary: 'var(--bs-primary)',
  info: 'var(--bs-info)',
  success: '#10b981',
  warning: '#f59e0b',
  danger: 'var(--bs-danger)',
  dark: '#334155',
  gray: '#9ca3af',
  secondary: '#cbd5e1',
};

const SECTION_BG = { background: '#f8f9fb', border: '1px solid #eef0f4' };

const statusBadgeClass = (variant) => {
  const map = {
    primary: '',
    info: '',
    success: '',
    warning: '',
    danger: '',
    dark: '',
    gray: 'border',
  };
  return map[variant] || '';
};

const DbqCaseManagerConditionCard = ({
  claimId,
  userDbq,
  isCollapsed = true,
  toeOptionsById,
  providerOptionsById,
  currentRequestNotes = '',
  hasUnsavedNotes = false,
  isSavingNotes = false,
  isDeleting = false,
  canAccessAudit = false,
  onToggleCollapse,
  onOpenEditor,
  onOpenEditRequest,
  onPreviewPdf,
  onDelete,
  onRequestNotesChange,
  onSaveRequestNotes,
  onRegeneratePacket,
  onRequestChanges,
  onFinalize,
  onReopen,
  isRegeneratingPacket = false,
  isPreviewingPdf = false,
  isRequestingChanges = false,
  isFinalizing = false,
  isReopening = false,
  showQaActions = false,
}) => {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [openingEvidenceFileId, setOpeningEvidenceFileId] = useState(null);

  const request = userDbq?.request || {};
  const selectedEvidenceFiles = Array.isArray(request.selectedEvidenceFiles)
    ? request.selectedEvidenceFiles
    : [];
  const claimIdForEvidence = userDbq?.claimId || claimId;
  const providerLabel = request.provider
    ? getWorkspacePersonLabel(request.provider)
    : request.providerUserId
      ? providerOptionsById.get(request.providerUserId)?.name || 'Assigned provider'
      : 'Not assigned';
  const accentColor =
    STATUS_ACCENT_COLORS[userDbq?.statusMeta?.variant] || STATUS_ACCENT_COLORS.secondary;
  const requestContentionRows = useMemo(
    () => getWorkspaceRequestContentionRows(userDbq, toeOptionsById || new Map()),
    [toeOptionsById, userDbq],
  );
  const toeSummary = useMemo(
    () => getWorkspaceToeSummary(userDbq, toeOptionsById || new Map()),
    [toeOptionsById, userDbq],
  );
  const showSubmissionPacketPanel =
    request.status === 'finalized' || Boolean(request?.submissionPacket?.ready);
  const isDraftRequest = request.status === 'draft_request';
  const canQaRequestChanges = showQaActions && request.status === 'in_qa_review';
  const canQaFinalize = showQaActions && request.status === 'in_qa_review';
  const canQaReopen = showQaActions && request.status === 'finalized';
  const title = useMemo(() => {
    if (Array.isArray(userDbq?.conditionLabels) && userDbq.conditionLabels.length > 0) {
      return userDbq.conditionLabels.join(', ');
    }
    return userDbq?.conditionTitle || userDbq?.title || 'DBQ Request';
  }, [userDbq]);

  const openEvidenceFile = useGetPresignedClaimFile((res) => {
    window.open(res.url, '_blank');
  });

  const handleOpenEvidenceFile = useCallback((file) => {
    if (!claimIdForEvidence || !file?.fileId) return;

    setOpeningEvidenceFileId(file.fileId);
    openEvidenceFile.mutate(
      { claimId: claimIdForEvidence, fileId: file.fileId },
      {
        onSettled: () => {
          setOpeningEvidenceFileId((currentFileId) =>
            currentFileId === file.fileId ? null : currentFileId,
          );
        },
      },
    );
  }, [claimIdForEvidence, openEvidenceFile]);

  return (
    <>
      <Card
        className="border-0 overflow-hidden"
        style={{ boxShadow: CARD_SHADOW }}
      >
        <div style={{ height: 3, background: accentColor }} />

        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div className="flex-grow-1 min-width-0">
              <div className="d-flex align-items-center gap-2 mb-1">
                <Badge
                  bg={userDbq?.statusMeta?.variant === 'gray' ? 'light' : (userDbq?.statusMeta?.variant || 'secondary')}
                  text={userDbq?.statusMeta?.variant === 'gray' ? 'dark' : undefined}
                  className={`rounded-pill px-2 py-1 ${statusBadgeClass(userDbq?.statusMeta?.variant)}`}
                  style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.02em' }}
                >
                  {userDbq?.statusMeta?.label}
                </Badge>
              </div>
              <h5 className="mb-1 fw-semibold" style={{ fontSize: '1.1rem' }}>
                {title}
              </h5>
              <div
                className="d-flex align-items-center flex-wrap gap-3"
                style={{ fontSize: '0.82rem', color: '#64748b' }}
              >
                <span>
                  <FontAwesomeIcon icon={faFileLines} className="me-1" style={{ opacity: 0.6 }} />
                  {userDbq?.title || 'Untitled DBQ'}
                </span>
                <span>
                  <FontAwesomeIcon icon={faUserMd} className="me-1" style={{ opacity: 0.6 }} />
                  {providerLabel}
                </span>
                {toeSummary && toeSummary !== 'Not selected' ? <span>TOEs: {toeSummary}</span> : null}
              </div>
              {requestContentionRows.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {requestContentionRows.map((requestContention) => (
                    <span
                      key={`${userDbq?.userDbqId}-${requestContention.contention}`}
                      className="px-2 py-1 rounded-pill"
                      style={{
                        fontSize: '0.72rem',
                        background: '#f8fafc',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {requestContention.contentionLabel}
                      {requestContention.toeLabel && requestContention.toeLabel !== 'Not selected'
                        ? ` • ${requestContention.toeLabel}`
                        : ''}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <Button
                variant="primary"
                size="sm"
                onClick={() => (
                  isDraftRequest
                    ? onOpenEditRequest?.(userDbq)
                    : onOpenEditor?.(userDbq)
                )}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                {isDraftRequest ? 'Complete Request' : 'Open DBQ'}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onPreviewPdf?.(userDbq)}
                disabled={isPreviewingPdf}
              >
                <FontAwesomeIcon
                  icon={isPreviewingPdf ? faSpinner : faFileLines}
                  spin={isPreviewingPdf}
                  className="me-1"
                />
                {isPreviewingPdf ? 'Previewing...' : 'Preview DBQ PDF'}
              </Button>
              <Button
                variant="light"
                size="sm"
                className="d-flex align-items-center justify-content-center"
                style={{ width: 32, height: 32, padding: 0, borderRadius: '50%' }}
                onClick={() => onToggleCollapse?.(userDbq?.userDbqId)}
                aria-expanded={!isCollapsed}
                aria-controls={`dbq-condition-details-${userDbq?.userDbqId}`}
                title={isCollapsed ? 'Show details' : 'Hide details'}
              >
                <FontAwesomeIcon
                  icon={isCollapsed ? faChevronDown : faChevronUp}
                  style={{ fontSize: '0.75rem' }}
                />
              </Button>
            </div>
          </div>

          {isCollapsed ? (
            <div
              className="d-flex align-items-center gap-3 flex-wrap mt-3 pt-3"
              style={{ borderTop: '1px solid #f1f5f9' }}
            >
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: '0.8rem', color: '#64748b' }}
              >
                <FontAwesomeIcon icon={faFolderOpen} style={{ opacity: 0.5 }} />
                <span>
                  {selectedEvidenceFiles.length} evidence file
                  {selectedEvidenceFiles.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: '0.8rem', color: '#64748b' }}
              >
                <FontAwesomeIcon icon={faLightbulb} style={{ opacity: 0.5 }} />
                <span>
                  {userDbq?.reviewCounts?.suggestionCount || 0} suggestion
                  {(userDbq?.reviewCounts?.suggestionCount || 0) !== 1 ? 's' : ''}
                </span>
              </div>
              <div
                className="d-flex align-items-center gap-2"
                style={{ fontSize: '0.8rem', color: '#64748b' }}
              >
                <FontAwesomeIcon icon={faCommentDots} style={{ opacity: 0.5 }} />
                <span>
                  {userDbq?.reviewCounts?.commentCount || 0} comment
                  {(userDbq?.reviewCounts?.commentCount || 0) !== 1 ? 's' : ''}
                </span>
              </div>
              {request.medicalOpinionParameters?.autoGenerateMedicalOpinion ? (
                <div
                  className="d-flex align-items-center gap-2"
                  style={{ fontSize: '0.8rem', color: '#64748b' }}
                >
                  <FontAwesomeIcon icon={faRobot} style={{ opacity: 0.5 }} />
                  <span>MO enabled</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {!isCollapsed && (
            <div id={`dbq-condition-details-${userDbq?.userDbqId}`}>
              <Row className="g-3 mt-2">
                <Col lg={4} xl={3}>
                  <div className="rounded-3 p-3 h-100" style={SECTION_BG}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <FontAwesomeIcon
                        icon={faFolderOpen}
                        style={{ color: 'var(--bs-primary)', opacity: 0.7, fontSize: '0.85rem' }}
                      />
                      <span
                        className="fw-semibold"
                        style={{ fontSize: '0.78rem', color: '#64748b' }}
                      >
                        EVIDENCE PACKAGE
                      </span>
                      {selectedEvidenceFiles.length > 0 ? (
                        <Badge
                          bg="light"
                          text="dark"
                          className="ms-auto rounded-pill"
                          style={{ fontSize: '0.68rem' }}
                        >
                          {selectedEvidenceFiles.length}
                        </Badge>
                      ) : null}
                    </div>

                    {selectedEvidenceFiles.length > 0 ? (
                      <div className="d-flex flex-column gap-1">
                        {selectedEvidenceFiles.slice(0, 3).map((file) => (
                          <Button
                            key={file.fileId}
                            variant="link"
                            className="p-0 text-start text-decoration-none d-flex align-items-center gap-2"
                            onClick={() => handleOpenEvidenceFile(file)}
                            disabled={openingEvidenceFileId === file.fileId}
                          >
                            <FontAwesomeIcon
                              icon={faFileLines}
                              style={{ opacity: 0.35, fontSize: '0.72rem', flexShrink: 0 }}
                            />
                            <span
                              className="text-truncate"
                              style={{ fontSize: '0.8rem', color: '#475569' }}
                            >
                              {file.fileName}
                            </span>
                          </Button>
                        ))}
                        {selectedEvidenceFiles.length > 3 ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none text-start"
                            style={{ fontSize: '0.78rem' }}
                            onClick={() => setShowEvidenceModal(true)}
                          >
                            +{selectedEvidenceFiles.length - 3} more
                          </Button>
                        ) : null}
                      </div>
                    ) : (
                      <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                        No evidence packaged yet.
                      </div>
                    )}

                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #eef0f4' }}>
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon
                          icon={faRobot}
                          style={{
                            fontSize: '0.78rem',
                            color: request.medicalOpinionParameters?.autoGenerateMedicalOpinion
                              ? 'var(--bs-primary)'
                              : '#cbd5e1',
                          }}
                        />
                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {request.medicalOpinionParameters?.autoGenerateMedicalOpinion
                            ? 'Medical Opinion enabled'
                            : 'No automation'}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={4} xl={5}>
                  <div className="rounded-3 p-3 h-100 d-flex flex-column" style={SECTION_BG}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <FontAwesomeIcon
                        icon={faStickyNote}
                        style={{ color: '#f59e0b', opacity: 0.8, fontSize: '0.85rem' }}
                      />
                      <span
                        className="fw-semibold"
                        style={{ fontSize: '0.78rem', color: '#64748b' }}
                      >
                        NOTES
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none ms-auto"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowNotesModal(true)}
                      >
                        Expand
                      </Button>
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      className="flex-grow-1"
                      style={{
                        fontSize: '0.85rem',
                        border: '1px solid #e2e8f0',
                        background: '#fff',
                        resize: 'vertical',
                      }}
                      value={currentRequestNotes}
                      placeholder="Add provider guidance, context, or review notes..."
                      onChange={(event) =>
                        onRequestNotesChange?.(userDbq?.userDbqId, event.target.value)
                      }
                      disabled={isSavingNotes}
                    />
                    {hasUnsavedNotes ? (
                      <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-decoration-none text-muted"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() =>
                            onRequestNotesChange?.(userDbq?.userDbqId, request.requestNotes || '')
                          }
                          disabled={isSavingNotes}
                        >
                          Discard
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => onSaveRequestNotes?.(userDbq)}
                          disabled={isSavingNotes}
                        >
                          {isSavingNotes ? 'Saving...' : 'Save Notes'}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={4} xl={4}>
                  <div className="rounded-3 p-3 h-100 d-flex flex-column" style={SECTION_BG}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <FontAwesomeIcon
                        icon={faCommentDots}
                        style={{ color: '#6366f1', opacity: 0.7, fontSize: '0.85rem' }}
                      />
                      <span
                        className="fw-semibold"
                        style={{ fontSize: '0.78rem', color: '#64748b' }}
                      >
                        REVIEW & ACTIVITY
                      </span>
                    </div>

                    <div className="d-flex gap-3 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: 30,
                            height: 30,
                            background:
                              (userDbq?.reviewCounts?.suggestionCount || 0) > 0
                                ? '#eef2ff'
                                : '#f1f5f9',
                            color:
                              (userDbq?.reviewCounts?.suggestionCount || 0) > 0
                                ? '#6366f1'
                                : '#94a3b8',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          {userDbq?.reviewCounts?.suggestionCount || 0}
                        </div>
                        <span style={{ fontSize: '0.78rem', color: '#475569' }}>
                          Suggestion{(userDbq?.reviewCounts?.suggestionCount || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: 30,
                            height: 30,
                            background:
                              (userDbq?.reviewCounts?.commentCount || 0) > 0
                                ? '#f0f9ff'
                                : '#f1f5f9',
                            color:
                              (userDbq?.reviewCounts?.commentCount || 0) > 0
                                ? '#0ea5e9'
                                : '#94a3b8',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          {userDbq?.reviewCounts?.commentCount || 0}
                        </div>
                        <span style={{ fontSize: '0.78rem', color: '#475569' }}>
                          Comment{(userDbq?.reviewCounts?.commentCount || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="pt-3 flex-grow-1" style={{ borderTop: '1px solid #eef0f4' }}>
                      <div
                        className="fw-semibold mb-2"
                        style={{
                          fontSize: '0.72rem',
                          color: '#94a3b8',
                          textTransform: 'uppercase',
                        }}
                      >
                        Recent Activity
                      </div>
                      <DbqRequestRecentActivity
                        userDbqId={userDbq?.userDbqId}
                        enabled={!isCollapsed && canAccessAudit}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              {showSubmissionPacketPanel ? (
                <DbqSubmissionPacketPanel
                  claimId={claimId}
                  userDbq={userDbq}
                  canRegenerate={request.status === 'finalized'}
                  onRegenerate={onRegeneratePacket}
                  isRegenerating={isRegeneratingPacket}
                />
              ) : null}

              <div
                className="d-flex flex-wrap gap-2 justify-content-end mt-3 pt-3"
                style={{ borderTop: '1px solid #f1f5f9' }}
              >
                {canQaRequestChanges ? (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => onRequestChanges?.(userDbq)}
                    disabled={isRequestingChanges}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                    {isRequestingChanges ? 'Requesting...' : 'Request Changes'}
                  </Button>
                ) : null}
                {canQaFinalize ? (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => onFinalize?.(userDbq)}
                    disabled={isFinalizing}
                    style={{ fontSize: '0.82rem' }}
                  >
                    {isFinalizing ? 'Finalizing...' : 'Mark Finalized'}
                  </Button>
                ) : null}
                {canQaReopen ? (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => onReopen?.(userDbq)}
                    disabled={isReopening}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                    {isReopening ? 'Reopening...' : 'Reopen'}
                  </Button>
                ) : null}
                {!isDraftRequest ? (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => onOpenEditRequest?.(userDbq)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-1" />
                    Edit Request
                  </Button>
                ) : null}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onDelete?.(userDbq)}
                  disabled={isDeleting}
                  style={{ fontSize: '0.82rem' }}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showNotesModal} onHide={() => setShowNotesModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Request Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">{title}</div>
          {currentRequestNotes ? (
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.6 }}>
              {currentRequestNotes}
            </div>
          ) : (
            <div className="text-muted">No notes have been added to this request yet.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEvidenceModal} onHide={() => setShowEvidenceModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Selected Evidence</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-3">{title}</div>
          {selectedEvidenceFiles.length > 0 ? (
            <div className="d-flex flex-column gap-3">
              {groupDbqEvidenceFilesByFolder(selectedEvidenceFiles).map((group) => (
                <div key={group.folderPath}>
                  <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                    {group.folderLabel}
                  </div>
                  <div className="d-flex flex-column gap-1 mt-2">
                    {group.files.map((file) => (
                      <Button
                        key={file.fileId}
                        variant="link"
                        className="p-0 text-start text-decoration-none d-flex align-items-center gap-2"
                        style={{ color: '#475569' }}
                        onClick={() => handleOpenEvidenceFile(file)}
                        disabled={openingEvidenceFileId === file.fileId}
                      >
                        <FontAwesomeIcon
                          icon={faFileLines}
                          style={{ opacity: 0.35, fontSize: '0.72rem', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: '0.85rem' }}>{file.fileName}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">No evidence packaged yet.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEvidenceModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DbqCaseManagerConditionCard;
